import CacheStore from 'services/utils/cacheStore';
import SingletonService from 'services/utils/singletonService';
import http from 'services/utils/http';
import { parseParamsForURL } from 'services/utils/urlParams';
import generalConfig from 'services/metaData/generalConfig';

const apiTiers = 'tiers';
const apiTierInstances = 'tiersInstance';

class Tiers {
    tiers = null;

    cacheTiers = CacheStore.getStore(apiTiers);

    cacheTierInstances = CacheStore.getStore(apiTierInstances);

    domain = '';

    constructor() {
        this.domain = generalConfig().sspPlatformJsDomain;
    }

    clearData() {
        this.cacheTierInstances.commit({
            type: 'clearCache',
            api: apiTierInstances
        });
        this.cacheTiers.commit({
            type: 'clearCache',
            api: apiTiers
        });
    }

    // eslint-disable-next-line class-methods-use-this
    formatData(data) {
        const res = { ...data };
        res.data.tiers = res.data.tiers.map((tierData) => {
            const tier = { ...tierData };
            if (!tier.instances) {
                tier.instances = [];
            } else {
                tier.instances = tier.instances.map((instanceData) => {
                    const instance = { ...instanceData };
                    if (instance.impressions) {
                        instance.impressions = parseFloat(instanceData.impressions);
                    }
                    if (instance.eCPM) {
                        const eCPM = parseFloat(instanceData.eCPM);
                        // eslint-disable-next-line no-restricted-globals
                        instance.eCPM = !isNaN(eCPM) ? eCPM : 0;
                    }
                    return instance;
                });
            }

            return tier;
        });

        if (res.data.bidders) {
            res.data.bidders = res.data.bidders.map((bidderData) => {
                const bidderInstance = { ...bidderData };
                if (bidderInstance.impressions) {
                    bidderInstance.impressions = parseFloat(bidderData.impressions);
                }
                if (bidderInstance.eCPM) {
                    bidderInstance.eCPM = parseFloat(bidderData.eCPM);
                }
                if (bidderInstance.revenue) {
                    bidderInstance.revenue = parseFloat(bidderData.revenue);
                }
                return bidderInstance;
            });
        }
        return res;
    }

    // <editor-fold desc="Get">
    async getTiers({ refresh, fields, groupId }) {
        const filters = { fields, groupId };
        if (!refresh) {
            const cachedResult = this.cacheTiers.getters.getFromCache(apiTiers, filters);
            if (cachedResult) {
                return cachedResult;
            }
        }

        const params = parseParamsForURL(filters);

        const apiUrl = `${this.domain}/api/v1/partners/tiers?${params}`;
        return http.get(apiUrl).then((res) => {
            this.cacheTiers.commit({
                type: 'setCache',
                api: apiTiers,
                req: filters,
                data: res
            });
            return res;
        });
    }

    async getTiersAndInstances({
        refresh, appKey, groupId, testGroup, country, startDate, endDate, deliveryType
    }) {
        const filters = {
            appKey, groupId, testGroup, country, startDate, endDate, deliveryType
        };
        if (!refresh) {
            const cachedResult = this.cacheTierInstances.getters.getFromCache(apiTierInstances,
                filters);
            if (cachedResult) {
                return cachedResult;
            }
        }

        const params = parseParamsForURL(filters);

        const apiUrl = `${this.domain}/api/v1/partners/tierInstances?${params}`;
        return http.get(apiUrl).then((resData) => {
            const res = this.formatData(resData);

            this.cacheTierInstances.commit({
                type: 'setCache',
                api: apiTierInstances,
                req: filters,
                data: res
            });
            return res;
        });
    }
    // </editor-fold>

    // <editor-fold desc="Update">
    async updateTierType({
        appKey, id, testGroup, startDate, endDate, type, deliveryType, groupId
    }) {
        const parsedParams = parseParamsForURL({
            appKey, id, testGroup, startDate, endDate, deliveryType, groupId
        });
        const apiUrl = `${this.domain}/api/v1/partners/tiers?${parsedParams}`;
        return http.put(apiUrl, { type }).then((res) => {
            this.clearData();
            return res;
        });
    }

    async addInstanceToTier({
        appKey, instanceId, tagId, providerId, deliveryType, groupId, tierId, testGroup,
        startDate, endDate
    }) {
        return this.updateInstance({
            appKey, instanceId, tagId, providerId, deliveryType, testGroup, startDate, endDate
        }, { groupId, tierId });
    }

    async updateInstanceConfig({
        appKey, instanceId, tagId, providerId, deliveryType, groupId, testGroup, startDate, endDate,
        iterationAdCap, tierId, manualPosition, country
    }) {
        const params = { tierId, manualPosition };
        if (iterationAdCap) {
            params.iterationAdCap = iterationAdCap;
        }

        return this.updateInstance({
            appKey,
            instanceId,
            tagId,
            providerId,
            deliveryType,
            groupId,
            testGroup,
            startDate,
            endDate,
            country
        }, params);
    }

    updateInstance(queryParams, data) {
        const parsedParams = parseParamsForURL(queryParams);
        const apiUrl = `${this.domain}/api/v1/partners/tierInstances?${parsedParams}`;
        return http.put(apiUrl, { configurations: [data] }).then((res) => {
            this.clearData();
            return this.formatData(res);
        });
    }
    // </editor-fold>

    // <editor-fold desc="Delete">
    async removeInstanceFromGroup({
        appKey, instanceId, tagId, providerId, deliveryType, groupId,
        tierId,
        startDate,
        endDate,
        testGroup
    }) {
        const queryParams = parseParamsForURL({
            appKey,
            instanceId,
            tagId,
            providerId,
            deliveryType,
            groupId,
            tierId,
            startDate,
            endDate,
            testGroup
        });

        const apiUrl = `${this.domain}/api/v1/partners/tierInstances?${queryParams}`;
        return http.delete(apiUrl).then((res) => {
            this.clearData();
            return this.formatData(res);
        });
    }

    async removeInstanceFromAllGroups({
        appKey, instanceId, tagId, providerId, deliveryType
    }) {
        const queryParams = parseParamsForURL({
            appKey, instanceId, tagId, providerId, deliveryType
        });

        const apiUrl = `${this.domain}/api/v1/partners/tierInstances?${queryParams}`;
        return http.delete(apiUrl).then((res) => {
            this.clearData();
            return res;
        });
    }
    // </editor-fold>
}

const singletonService = new SingletonService(apiTiers);
if (!singletonService.isInitialized()) {
    singletonService.setService(new Tiers());
}

export default singletonService.getService();
